import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const API = '/api';

/**
 * useVoiceInput - Hook for recording audio and transcribing via OpenAI Whisper
 * Returns: { isRecording, isTranscribing, duration, startRecording, stopRecording, cancelRecording }
 */
export function useVoiceInput({ onTranscript }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setDuration(0);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 } 
      });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setDuration(0);

      // Duration timer
      const start = Date.now();
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - start) / 1000));
      }, 200);

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
      } else {
        toast.error('Could not access microphone: ' + err.message);
      }
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') return;

    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;
      
      mediaRecorder.onstop = async () => {
        setIsRecording(false);
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
        
        if (chunksRef.current.length === 0) { resolve(); return; }
        
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        
        if (blob.size < 1000) { 
          toast.error('Recording too short. Hold the button longer.');
          resolve(); 
          return; 
        }

        // Transcribe
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('audio', blob, 'recording.webm');
          
          const res = await fetch(`${API}/ai/transcribe`, {
            method: 'POST',
            credentials: 'include',
            body: formData
          });

          if (res.ok) {
            const data = await res.json();
            if (data.text?.trim()) {
              onTranscript(data.text.trim());
            } else {
              toast.info('No speech detected. Try again.');
            }
          } else {
            const err = await res.json().catch(() => ({}));
            toast.error(err.detail || 'Transcription failed');
          }
        } catch (e) {
          toast.error('Transcription error: ' + e.message);
        } finally {
          setIsTranscribing(false);
        }
        resolve();
      };

      mediaRecorder.stop();
    });
  }, [onTranscript]);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = () => {}; // Prevent transcription
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    cleanup();
  }, [cleanup]);

  return { isRecording, isTranscribing, duration, startRecording, stopRecording, cancelRecording };
}
